<template>
  <div :class="[
         $style.loader,
         behindModal && $style.loaderBehindModal
       ]"
       data-testid="app-loader"
  >
    <span :class="$style.loaderContent">
      <b-lottie name="loader" :height="50" :width="50"/>
    </span>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { useVersionCheck } from '@booksy-com/vue-tools'
import { DATADOG_CUSTOM_ACTIONS_NAMES, FEATURE_FLAGS } from '@/lib/consts.js'
import { useModal } from '@/composables/modal'
import { datadogRum } from '@datadog/browser-rum'
import { useCore } from '@/composables/core'
import { useEppo, whenEppoIsReady } from '@/composables/eppo'

export default {
  props: {
    defaultTimeout: Number,
    noTimeout: Boolean,
  },

  setup () {
    const modal = useModal()

    return {
      modal,
    }
  },

  data () {
    return {
      timeoutHandler: null,
      behindModal: false,
    }
  },

  computed: {
    ...mapState({
      isLoading: state => state.loadingScreen.count,
      globalLoaderTimeout: state => state.loadingScreen.timeout,
      timeoutDisabled: state => state.loadingScreen.timeoutDisabled,
      business: state => state.business.business,
      webView: state => state.app.webView,
    }),
    timeout () {
      return this.defaultTimeout || this.globalLoaderTimeout
    },
    hideTimeout () {
      return this.noTimeout || this.timeoutDisabled
    },
  },

  methods: {
    openModal () {
      if (this.hideTimeout) {
        return
      }

      whenEppoIsReady.then(() => {
        try {
          if (useEppo().ff(FEATURE_FLAGS.Logging_Booksy_Unresponsive_Message)) {
            const { refreshRequired } = useVersionCheck()
            const { store } = useCore()

            /* https://booksy.atlassian.net/browse/PET-801 - we have mysterious bug with freeze app, we want to collect logs from that */
            datadogRum.addAction(DATADOG_CUSTOM_ACTIONS_NAMES.BOOKSY_UNRESPONSIVE_MESSAGE, {
              businessId: store.state.business.business.id,
              componentPath: 'src/components/common/b-loader.vue',
              refreshRequired: refreshRequired.value,
              webview: store.state.app.webView,
            })
          }
        } catch (e) {}
      })

      this.behindModal = true

      this.modal.open({
        onCancel: this.reset,
        width: 400,
        content: {
          $title: this.$t('components.common.b-loader.modal-title'),
          $text: this.$t('components.common.b-loader.modal-description'),
        },
        buttons: [
          {
            $text: this.$t('components.common.b-loader.wait'),
            theme: 'primary',
            size: 'lg',
            '@click': this.reset,
          },
          {
            $text: this.$t('components.common.b-loader.reload'),
            theme: 'primary',
            size: 'lg',
            '@click': this.reload,
          },
        ],
      })
    },
    initTimeout () {
      if (!this.hideTimeout) {
        this.timeoutHandler = setTimeout(() => {
          this.openModal()
        }, this.timeout)
      }
    },
    reset () {
      this.modal.close()
      if (this.isLoading) {
        this.initTimeout()
      }
    },
    reload () {
      window.location.reload()
    },
  },

  created () {
    if (this.defaultTimeout !== 0) {
      this.initTimeout()
    }
  },

  beforeUnmount () {
    clearTimeout(this.timeoutHandler)
    this.behindModal = false
  },
}
</script>

<style lang="scss" module>
  .loader {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 16777271;
    background: var(--color-white);
    opacity: .3;

    &BehindModal {
      z-index: 10000;
    }
    &Content {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      padding: 16px;
    }
  }
</style>
